// src/SignInWallModal.js — dismissible sign-in nudge (SW.6 D1 reversed)
// Web parity of SignInWallView.swift. Shown at recurring thresholds (20/≥5-positive, then every 50
// votes) for anonymous users. Dismissible with "Not now" — re-appears at the next threshold.
import React, { useState, useRef } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { requestOtpCode, verifyOtpCode } from './v2Api';
import './SignInWallModal.css';

const SignInWallModal = ({ auth, voteTotal, votePositive, onSignedIn, onDismiss, reason = 'threshold', resendCooldownSecs = 60 }) => {
  const [phase, setPhase] = useState('email'); // 'email' | 'code' | 'done'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [subscribeOptIn, setSubscribeOptIn] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimer = useRef(null);

  const startCooldown = () => {
    setResendCooldown(resendCooldownSecs);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownTimer.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const sendCode = async () => {
    if (!email.includes('@')) return;
    if (!consentAccepted) { setConsentError(true); return; }
    setError('');
    setBusy(true);
    try {
      const { ok, data } = await requestOtpCode(email);
      if (!ok) {
        setError(data.error || 'Failed to send code — please try again.');
        setBusy(false);
        return;
      }
      setPhase('code');
      startCooldown();
    } catch {
      setError('Network error — check your connection.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    if (code.length < 6) return;
    setError('');
    setBusy(true);
    try {
      const { ok, data } = await verifyOtpCode({ email, code, subscribe: subscribeOptIn, consentAccepted });
      if (!ok) {
        const msg =
          data.error === 'expired'     ? 'Code expired — request a new one.' :
          data.error === 'attempt_cap' ? 'Too many attempts — request a new code.' :
                                         'Incorrect code — check your email and try again.';
        setError(msg);
        setBusy(false);
        return;
      }
      const credential = await signInWithCustomToken(auth, data.token);
      setPhase('done');
      setTimeout(() => onSignedIn(credential.user), 1500);
    } catch {
      setError('Sign-in failed — check your connection.');
    } finally {
      setBusy(false);
    }
  };

  const resend = () => {
    setCode('');
    setError('');
    sendCode();
  };

  if (phase === 'done') {
    return (
      <div className="signin-overlay">
        <div className="signin-card signin-card--centered">
          <div style={{ fontSize: 56 }}>✅</div>
          <h2 style={{ color: 'var(--c-text)', margin: 0 }}>Signed in!</h2>
          <p style={{ color: 'var(--c-text-secondary)', textAlign: 'center' }}>Your picks are saved. Keep exploring!</p>
        </div>
      </div>
    );
  }

  if (phase === 'code') {
    return (
      <div className="signin-overlay">
        <div className="signin-card">
          <h2 style={{ color: 'var(--c-text)', margin: 0 }}>Enter your 6-digit code</h2>
          <p style={{ color: 'var(--c-text-secondary)', margin: 0 }}>
            We sent a code to <strong style={{ color: 'var(--c-text)' }}>{email}</strong>. It expires in 10 minutes.
          </p>
          <input
            id="code-field"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => e.key === 'Enter' && code.length === 6 && !busy && verifyCode()}
            placeholder="000000"
            className="signin-code-input"
          />
          {error && <p className="otp-error" style={{ margin: 0, fontSize: 13 }}>{error}</p>}
          <button
            id="verify-button"
            onClick={verifyCode}
            disabled={busy || code.length < 6}
            className="active-button"
            style={{ padding: '14px 0', fontSize: 16, opacity: code.length < 6 ? 0.5 : 1 }}
          >
            {busy ? 'Verifying…' : 'Confirm code'}
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            {resendCooldown > 0
              ? <span className="signin-resend-cooldown">Resend in {resendCooldown}s</span>
              : <button id="resend-code-button" onClick={resend} className="secondary-button" disabled={busy} style={{ fontSize: 13 }}>Resend code</button>
            }
            <button
              onClick={() => { setPhase('email'); setCode(''); setError(''); }}
              className="secondary-button"
              style={{ fontSize: 13 }}
            >
              Change email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signin-overlay">
      {onDismiss && (
        <button
          id="wall-not-now-button"
          onClick={onDismiss}
          className="signin-not-now"
        >
          Not now
        </button>
      )}
      <div className="signin-card">
        <h2 style={{ color: 'var(--c-text)', margin: 0 }}>Save your picks</h2>
        {reason !== 'manual' && (
          <p style={{ color: 'var(--c-accent)', fontWeight: 600, margin: 0, fontSize: 16 }}>
            You've reviewed {voteTotal} event{voteTotal === 1 ? '' : 's'} and liked {votePositive}.
          </p>
        )}
        <p style={{ color: 'var(--c-text-secondary)', margin: 0, fontSize: 14, lineHeight: 1.6 }}>
          {reason === 'manual'
            ? 'Sign in to save your picks across devices.'
            : 'Save your picks. Verify your email once to restore your selection on any device.'}
        </p>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && email.includes('@') && !busy && sendCode()}
          id="email-field"
          placeholder="your@email.com"
          className="signin-text-input"
        />
        {/* Consent checkbox (required — gates send) */}
        <div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={e => { setConsentAccepted(e.target.checked); if (e.target.checked) setConsentError(false); }}
              style={{ marginTop: 2, width: 18, height: 18, accentColor: 'var(--c-accent)', flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>
              I agree to the{' '}
              <a href="/privacy-policy" target="_blank" rel="noreferrer" className="signin-consent-link">Privacy Policy</a>
              {' '}and{' '}
              <a href="/terms-of-use" target="_blank" rel="noreferrer" className="signin-consent-link">Terms of Use</a>.
            </span>
          </label>
          {consentError && (
            <p style={{ color: 'var(--c-error)', fontSize: 12, margin: '4px 0 0 28px' }}>⊘ Please agree to continue.</p>
          )}
        </div>
        {/* Subscribe checkbox (optional, default off — GDPR) */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={subscribeOptIn}
            onChange={e => setSubscribeOptIn(e.target.checked)}
            style={{ marginTop: 2, width: 18, height: 18, accentColor: 'var(--c-accent)', flexShrink: 0 }}
          />
          <span style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>Email me occasional app updates.</span>
        </label>
        {error && <p style={{ color: 'var(--c-error)', margin: 0, fontSize: 12 }}>{error}</p>}
        <button
          onClick={sendCode}
          disabled={busy || !email.includes('@')}
          id="send-code-button"
          className="active-button"
          style={{
            padding: '14px 0', fontSize: 16,
            opacity: !email.includes('@') || !consentAccepted ? 0.5 : 1,
          }}
        >
          {busy ? 'Sending…' : 'Send code'}
        </button>
        <p className="signin-footer-text">
          No password needed. We'll email you a 6-digit code.
        </p>
      </div>
    </div>
  );
};

export default SignInWallModal;
